import apiClient from './api';

/*
 * Device flags: checks super admins define that MDM runs on device events, a
 * schedule or on demand. Shapes mirror internal/service/flags; see
 * docs/superpowers/specs/2026-10-08-device-flags-design.md.
 */

// ── Types ──

export type FlagSeverity = 'warning' | 'critical';
export type FlagStatus = 'draft' | 'enabled' | 'disabled';
export type FlagTrigger = 'enrolled' | 'online' | 'booted' | 'schedule';
export type FlagState = 'flagged' | 'clear' | 'error';
export type FlagCheckKind = 'field' | 'command';
export type FlagOp =
  | 'is_empty'
  | 'not_empty'
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'matches'
  | 'not_matches'
  | 'exit_nonzero';

export interface FlagCheck {
  kind: FlagCheckKind;
  field?: string;
  command?: string;
  op: FlagOp;
  value?: string;
  /** Ignore case, ':' and '-' when comparing. Defaults on for (not_)equals and (not_)contains. */
  normalize?: boolean;
  timeout_ms?: number;
}

/** Which machines a flag covers. Empty fields mean any; an empty scope is every machine. */
export interface DeviceScope {
  tenant_ids?: string[];
  types?: string[];
  sub_types?: string[];
  models?: string[];
  os_versions?: string[];
  agent_versions?: string[];
  is_internal?: boolean;
}

export interface FlagCounts {
  flagged: number;
  clear: number;
  error: number;
}

export interface DeviceFlag {
  id: string;
  name: string;
  severity: FlagSeverity;
  message: string;
  scope: DeviceScope;
  check: FlagCheck;
  triggers: FlagTrigger[];
  schedule_hours?: number;
  slack: boolean;
  status: FlagStatus;
  /** A test run passed for the current check; enabling requires it. */
  tested: boolean;
  enabled_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  counts: FlagCounts;
}

export interface DeviceFlagInput {
  name: string;
  severity: FlagSeverity;
  message: string;
  scope: DeviceScope;
  check: FlagCheck;
  triggers: FlagTrigger[];
  schedule_hours?: number;
  slack: boolean;
}

export interface FlagVerdict {
  state: FlagState;
  expected?: string;
  error?: string;
}

export interface FlagTestResult {
  stdout: string;
  stderr: string;
  exit_code?: number;
  duration_ms: number;
  verdict: FlagVerdict;
}

export interface FlagResult {
  device_id: string;
  machine_id: string;
  tenant_id: string;
  model: string;
  device_status: string;
  last_online?: string;
  state: FlagState;
  output: string;
  error: string;
  trigger: FlagTrigger | 'manual';
  exit_code?: number;
  checked_at: string;
  flagged_since?: string;
}

export interface FlagResultsPage {
  items: FlagResult[];
  /** Results matching the state and search filter. */
  total: number;
  /** Machines the flag covers, checked or not. */
  scope_total: number;
}

export interface FlagResultsParams {
  state?: FlagState;
  search?: string;
  limit?: number;
  offset?: number;
}

/** A flag named on a device row (super admins only). */
export interface FlagRef {
  id: string;
  name: string;
  severity: FlagSeverity;
}

/** A flag currently flagging one machine, for its device page. */
export interface DeviceFlagDetail {
  flag_id: string;
  name: string;
  severity: FlagSeverity;
  message: string;
  check: FlagCheck;
  state: FlagState;
  output: string;
  error: string;
  trigger: FlagTrigger | 'manual';
  checked_at: string;
  flagged_since?: string;
}

// ── Service ──

const deviceFlagService = {
  list: () =>
    apiClient.get<{ items: DeviceFlag[] }>('/admin/device-flags').then((r) => r.items ?? []),

  get: (id: string) => apiClient.get<DeviceFlag>(`/admin/device-flags/${id}`),

  create: (data: DeviceFlagInput) => apiClient.post<DeviceFlag>('/admin/device-flags', data),

  update: (id: string, data: DeviceFlagInput) =>
    apiClient.put<DeviceFlag>(`/admin/device-flags/${id}`, data),

  remove: (id: string) => apiClient.delete<void>(`/admin/device-flags/${id}`),

  /** Runs the check once on one machine (devices.id or machine ID) and waits. */
  test: (id: string, deviceId: string) =>
    apiClient.post<FlagTestResult>(`/admin/device-flags/${id}/test`, { device_id: deviceId }),

  enable: (id: string) => apiClient.post<DeviceFlag>(`/admin/device-flags/${id}/enable`),

  disable: (id: string) => apiClient.post<DeviceFlag>(`/admin/device-flags/${id}/disable`),

  /** Queues every machine in scope; returns how many were queued. */
  run: (id: string) =>
    apiClient.post<{ queued: number }>(`/admin/device-flags/${id}/run`).then((r) => r.queued),

  results: (id: string, params: FlagResultsParams) =>
    apiClient.get<FlagResultsPage>(`/admin/device-flags/${id}/results`, { params }),

  recheck: (id: string, deviceId: string) =>
    apiClient.post<void>(`/admin/device-flags/${id}/results/${deviceId}/recheck`),

  forDevice: (deviceId: string) =>
    apiClient
      .get<{ items: DeviceFlagDetail[] }>(`/admin/devices/${deviceId}/flags`)
      .then((r) => r.items ?? []),
};

export default deviceFlagService;
